import { AllPathsState } from '@/types';
import { AllPathsPickedChain } from '@/types/api/AllPathsPickedChain';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

// ================================ Initial State ================================ //

const initialState: AllPathsState = {
  scenarioId: null,
  chains: [],
  operations: {},
  picked_chains: [],
  visibleChains: {},
};

// ================================ Private Methods ================================ //

const _resetState = (initialState: AllPathsState) => initialState;

// ================================ Reducers ================================ //

const setIteration = (
  state: AllPathsState,
  action: PayloadAction<{
    scenarioId: string;
    chains: AllPathsState['chains'];
    operations: AllPathsState['operations'];
    picked_chains?: AllPathsState['picked_chains'];
  }>,
) => {
  state.chains = action.payload.chains;
  state.operations = action.payload.operations;
  if (action.payload.picked_chains) {
    state.picked_chains = action.payload.picked_chains;

    state.visibleChains = action.payload.picked_chains.reduce(
      (acc: AllPathsState['visibleChains'], chain: AllPathsPickedChain) => {
        acc[chain.generic_symbol] = state.visibleChains[chain.chain_id] ?? true;
        return acc;
      },
      {},
    );
  }
};

const addChains = (
  state: AllPathsState,
  action: PayloadAction<{
    chains: AllPathsState['chains'];
    operations: AllPathsState['operations'];
  }>,
) => {
  state.chains.push(...action.payload.chains);
  state.operations = { ...state.operations, ...action.payload.operations };
};

const setPickedChains = (
  state: AllPathsState,
  action: PayloadAction<AllPathsPickedChain[]>,
) => {
  state.picked_chains = action.payload;
  state.visibleChains = action.payload.reduce(
    (acc: AllPathsState['visibleChains'], chain: AllPathsPickedChain) => {
      acc[chain.chain_id] = state.visibleChains[chain.chain_id] ?? true;
      return acc;
    },
    {},
  );
};

const setChainVisibility = (
  state: AllPathsState,
  action: PayloadAction<{
    chainSymbol: string;
    visible: boolean;
  }>,
) => {
  state.visibleChains[action.payload.chainSymbol] = action.payload.visible;
};

const hideAllChains = (state: AllPathsState) => {
  state.visibleChains = state.picked_chains.reduce(
    (acc: AllPathsState['visibleChains'], chain: AllPathsPickedChain) => {
      acc[chain.chain_id] = false;
      return acc;
    },
    {},
  );
};

const showAllChains = (state: AllPathsState) => {
  state.visibleChains = state.picked_chains.reduce(
    (acc: AllPathsState['visibleChains'], chain: AllPathsPickedChain) => {
      acc[chain.chain_id] = true;
      return acc;
    },
    {},
  );
};

// ================================ Selectors ================================ //

const selectState = (state: { allPaths: AllPathsState } | undefined) =>
  state?.allPaths ?? initialState;

export const selectAllPathsChains = createSelector(
  selectState,
  (allPaths) => allPaths.chains,
);

export const selectAllPathsOperations = createSelector(
  selectState,
  (allPaths) => allPaths.operations,
);

export const selectAllPathsPickedChains = createSelector(
  selectState,
  (allPaths) => allPaths.picked_chains,
);

export const selectAllPathsVisibleChains = createSelector(
  selectState,
  (allPaths) => allPaths.visibleChains,
);

// ================================ Slice ================================ //

const _allPathsReducers = (initialState: AllPathsState) => ({
  resetState: () => _resetState(initialState),
  setIteration,
  addChains,
  setPickedChains,
  setChainVisibility,
  hideAllChains,
  showAllChains,
});

const reducers = _allPathsReducers(initialState);

export const allPathsSlice = createSlice({
  name: 'allPaths',
  initialState,
  reducers,
});

export const allPathsActions = allPathsSlice.actions;

export default allPathsSlice.reducer;
